/**
 * A dictation mic for the Otto composer / reply inputs. Renders nothing when
 * NEITHER dictation engine is available — live speech recognition or the
 * recorded fallback (see useSpeechToText) — so it never shows a dead control.
 * While listening it turns red; while the recorded clip is at the server
 * transcriber it shows a busy spinner (one clip at a time). A recognised or
 * transcribed phrase is appended to the field via the `onText` callback.
 */
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import type { JSX } from 'react';

import { useSpeechToText } from './useSpeechToText.js';

export function MicButton({
  onText,
  size = 'small',
}: {
  /** Called with a recognised phrase (append it to the field). */
  onText: (text: string) => void;
  size?: 'small' | 'medium';
}): JSX.Element | null {
  const { supported, listening, transcribing, toggle } = useSpeechToText(onText);
  if (!supported) return null;
  const label = transcribing ? 'Transcribing…' : listening ? 'Stop dictation' : 'Dictate';
  return (
    <Tooltip title={label}>
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          onClick={toggle}
          size={size}
          disabled={transcribing}
          aria-label={listening ? 'Stop dictation' : 'Dictate your request'}
          data-testid="otto-mic"
          sx={{ color: listening ? 'error.main' : 'text.secondary' }}
        >
          {transcribing ? (
            <CircularProgress
              size={size === 'small' ? 16 : 20}
              thickness={5}
              data-testid="otto-mic-transcribing"
            />
          ) : listening ? (
            <MicRoundedIcon fontSize={size} />
          ) : (
            <MicNoneRoundedIcon fontSize={size} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}
